import * as React from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2, RefreshCw, History } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Id } from '@/convex/_generated/dataModel';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { extractAnalyticsMetrics } from '@/utils/uploadPostResponses';

interface PostAnalyticsProps {
	postId: string;
	platform: string;
	profileKey: string;
	contentHistoryId: Id<'contentHistory'>;
	requestId?: string;
	className?: string;
}

interface AnalyticsHistoryEntry {
	timestamp: number;
	likes?: number;
	comments?: number;
	shares?: number;
	views?: number;
	quotes?: number;
}

function AnalyticsHistoryDialog({
	contentHistoryId,
}: {
	contentHistoryId: Id<'contentHistory'>;
}) {
	const [page, setPage] = React.useState(1);
	const itemsPerPage = 10;

	const analyticsHistory = useQuery(api.analytics.getAnalyticsHistory, {
		contentHistoryId,
		limit: 100, // Get enough for pagination
	});

	const formatNumber = (num: number | undefined) => {
		if (num === undefined) return '0';
		return num.toLocaleString();
	};

	const formatTimestamp = (timestamp: number) => {
		return format(new Date(timestamp), 'MMM d, yyyy h:mm a');
	};

	const totalPages = Math.ceil(
		(analyticsHistory?.length || 0) / itemsPerPage
	);
	const paginatedHistory = analyticsHistory?.slice(
		(page - 1) * itemsPerPage,
		page * itemsPerPage
	);

	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button
					variant='outline'
					size='sm'
					className='bg-gradient-to-b from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white'
				>
					<History className='w-4 h-4 mr-2' />
					View History
				</Button>
			</DialogTrigger>
			<DialogContent className='max-w-5xl max-h-[80vh] overflow-hidden flex flex-col bg-gradient-to-b from-gray-900 to-gray-950 border-purple-500/20'>
				<DialogHeader className='px-6 pt-6'>
					<DialogTitle className='text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 text-transparent bg-clip-text animate-gradient'>
						✨ Analytics History
					</DialogTitle>
					<p className='text-gray-400 mt-2'>
						Track your post's performance over time
					</p>
				</DialogHeader>
				<div className='flex-1 overflow-hidden flex flex-col min-h-0'>
					<div className='px-6'>
						<div className='grid grid-cols-[2fr,1fr,1fr,1fr,1fr,1fr] gap-6 text-sm font-medium text-gray-400 mb-2 whitespace-nowrap border-b border-gray-800 pb-2 sticky top-0 bg-gradient-to-b from-gray-900 to-gray-950'>
							<div>📅 Timestamp</div>
							<div>❤️ Likes</div>
							<div>🔭 Views</div>
							<div>🔄 Retweets</div>
							<div>💬 Comments</div>
							<div>💭 Quotes</div>
						</div>
					</div>
					<div className='flex-1 overflow-y-auto px-6 min-h-0'>
						<div className='space-y-1'>
							{paginatedHistory?.map(
								(
									entry: AnalyticsHistoryEntry,
									index: number
								) => (
									<div
										key={entry.timestamp}
										className={cn(
											'grid grid-cols-[2fr,1fr,1fr,1fr,1fr,1fr] gap-6 p-3 rounded-lg text-sm whitespace-nowrap transition-colors duration-200',
											index === 0 && page === 1
												? 'bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10'
												: 'hover:bg-gray-800/50'
										)}
									>
										<div className='font-medium text-gray-300'>
											{formatTimestamp(entry.timestamp)}
										</div>
										<div className='text-pink-400'>
											{formatNumber(entry.likes)}
										</div>
										<div className='text-purple-400'>
											{formatNumber(entry.views)}
										</div>
										<div className='text-green-400'>
											{formatNumber(entry.shares)}
										</div>
										<div className='text-blue-400'>
											{formatNumber(entry.comments)}
										</div>
										<div className='text-amber-400'>
											{formatNumber(entry.quotes)}
										</div>
									</div>
								)
							)}
						</div>
					</div>
					{totalPages > 1 && (
						<div className='p-4 border-t border-gray-800 mt-auto'>
							<div className='flex items-center justify-between px-2'>
								<div className='text-sm text-gray-400'>
									Page {page} of {totalPages}
								</div>
								<div className='flex gap-2'>
									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											setPage((p) => Math.max(1, p - 1))
										}
										disabled={page === 1}
										className='bg-gradient-to-b from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white'
									>
										Previous
									</Button>
									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											setPage((p) =>
												Math.min(totalPages, p + 1)
											)
										}
										disabled={page === totalPages}
										className='bg-gradient-to-b from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white'
									>
										Next
									</Button>
								</div>
							</div>
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}

export function PostAnalytics({
	postId,
	platform,
	profileKey,
	contentHistoryId,
	requestId,
	className,
}: PostAnalyticsProps) {
	const { toast } = useToast();
	const [isRefreshing, setIsRefreshing] = React.useState(false);
	const getPostAnalytics = useAction(api.uploadPost.getPostAnalytics);
	const storePostAnalytics = useMutation(api.analytics.storePostAnalytics);
	const storedAnalytics = useQuery(api.analytics.getStoredAnalytics, {
		contentHistoryId,
	});

	const handleRefreshAnalytics = async () => {
		try {
			setIsRefreshing(true);
				const analyticsArgs = {
					postId,
					platform,
					profileKey,
					...(requestId ? { requestId } : {}),
				};
				const analytics = await getPostAnalytics(analyticsArgs);

			if (analytics) {
				const data = extractAnalyticsMetrics(analytics);
				await storePostAnalytics({
					contentHistoryId,
					postId,
					platform,
					profileKey,
					analytics: {
						likes: data.likes,
						comments: data.comments,
						shares: data.shares,
						views: data.views,
						quotes: data.quotes,
					},
				});
				toast({
					title: '✨ Analytics Updated',
					description: 'Post analytics have been refreshed',
					duration: 3000,
				});
			} else {
				toast({
					title: 'Analytics Pending',
					description:
						'No analytics were returned for this post yet. Try again after the social platform has processed the post.',
					duration: 4000,
				});
			}
		} catch (error) {
			console.error('Error refreshing analytics:', error);
			toast({
				title: 'Error',
				description:
					error instanceof Error
						? error.message
						: 'Failed to refresh analytics',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsRefreshing(false);
		}
	};

	const formatNumber = (num: number | undefined) => {
		if (num === undefined) return '0';
		return num.toLocaleString();
	};

	const getLastUpdatedTime = () => {
		if (!storedAnalytics?.lastUpdated) return 'Never';
		const date = new Date(storedAnalytics.lastUpdated);
		return date.toLocaleString();
	};

	return (
		<div className={cn('space-y-4', className)}>
			<div className='flex items-center justify-between'>
				<div className='text-sm text-gray-500'>
					Last updated: {getLastUpdatedTime()}
				</div>
				<div className='flex items-center gap-2'>
					<AnalyticsHistoryDialog
						contentHistoryId={contentHistoryId}
					/>
					<Button
						variant='outline'
						size='sm'
						onClick={handleRefreshAnalytics}
						disabled={isRefreshing}
						className='bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white'
					>
						{isRefreshing ? (
							<>
								<Loader2 className='w-4 h-4 mr-2 animate-spin' />
								Refreshing...
							</>
						) : (
							<>
								<RefreshCw className='w-4 h-4 mr-2' />
								Refresh Stats
							</>
						)}
					</Button>
				</div>
			</div>
			<div className='grid grid-cols-5 gap-4'>
				<div className='bg-gradient-to-b from-pink-400/10 to-pink-600/10 rounded-lg p-4'>
					<div className='text-sm text-gray-500 mb-1'>❤️ Likes</div>
					<div className='text-2xl font-bold bg-gradient-to-b from-pink-400 to-pink-600 text-transparent bg-clip-text'>
						{formatNumber(storedAnalytics?.likes)}
					</div>
				</div>
				<div className='bg-gradient-to-b from-purple-400/10 to-purple-600/10 rounded-lg p-4'>
					<div className='text-sm text-gray-500 mb-1'>🔭 Views</div>
					<div className='text-2xl font-bold bg-gradient-to-b from-purple-400 to-purple-600 text-transparent bg-clip-text'>
						{formatNumber(storedAnalytics?.views)}
					</div>
				</div>
				<div className='bg-gradient-to-b from-green-400/10 to-green-600/10 rounded-lg p-4'>
					<div className='text-sm text-gray-500 mb-1'>
						🔄 Retweets
					</div>
					<div className='text-2xl font-bold bg-gradient-to-b from-green-400 to-green-600 text-transparent bg-clip-text'>
						{formatNumber(storedAnalytics?.shares)}
					</div>
				</div>
				<div className='bg-gradient-to-b from-blue-400/10 to-blue-600/10 rounded-lg p-4'>
					<div className='text-sm text-gray-500 mb-1'>
						💬 Comments
					</div>
					<div className='text-2xl font-bold bg-gradient-to-b from-blue-400 to-blue-600 text-transparent bg-clip-text'>
						{formatNumber(storedAnalytics?.comments)}
					</div>
				</div>
				<div className='bg-gradient-to-b from-yellow-400/10 to-amber-600/10 rounded-lg p-4'>
					<div className='text-sm text-gray-500 mb-1'>💭 Quotes</div>
					<div className='text-2xl font-bold bg-gradient-to-b from-yellow-400 to-amber-600 text-transparent bg-clip-text'>
						{formatNumber(storedAnalytics?.quotes)}
					</div>
				</div>
			</div>
		</div>
	);
}
